import React, { FC } from "react";
import { Link } from "react-router-dom";

const DeletionInstructions: FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src="/logo_text.png" alt="Code Coast" className="h-8" />
            </Link>
            <Link
              to="/"
              className="text-[#14b7cd] uppercase text-sm px-6 py-2 border border-[#14b7cd] hover:bg-[#14b7cd] hover:text-white transition-colors duration-300"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">
              User Data Deletion Instructions
            </h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                At Code Coast, we respect your privacy and give you full control over your personal data. 
                This page explains how you can request the deletion of your account and associated data.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Request Data Deletion</h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Important:</strong> Deleting your account will permanently remove all your data, 
                      including your progress, certificates, and course enrollments. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Method 1: Through Your Account Settings</h3>
              <ol className="list-decimal list-inside space-y-3 mb-8 text-gray-700">
                <li>Log in to your Code Coast account</li>
                <li>Navigate to your profile settings or account settings</li>
                <li>Look for the "Delete Account" or "Data Management" section</li>
                <li>Follow the prompts to confirm your account deletion</li>
                <li>Your account and data will be permanently deleted within 30 days</li>
              </ol>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Method 2: Contact Support</h3>
              <p className="text-gray-700 mb-4">
                If you cannot access your account or prefer to contact us directly, you can request data deletion by:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li><strong>Email:</strong> <a href="mailto:privacy@codecoast.org" className="text-[#14b7cd] hover:underline">privacy@codecoast.org</a></li>
                <li><strong>Support Email:</strong> <a href="mailto:support@codecoast.org" className="text-[#14b7cd] hover:underline">support@codecoast.org</a></li>
                <li><strong>Phone:</strong> +233 24 086 5997</li>
              </ul>

              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h4 className="font-semibold text-gray-900 mb-3">Include in Your Request:</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  <li>Your full name and email address associated with the account</li>
                  <li>Clear statement that you want to delete your account and all associated data</li>
                  <li>Confirmation that you understand this action is irreversible</li>
                  <li>Any additional information that might help us locate your account</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">What Data Will Be Deleted</h2>
              <p className="text-gray-700 mb-4">
                When you request account deletion, the following data will be permanently removed:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li>Personal information (name, email, phone number)</li>
                <li>Account credentials and authentication data</li>
                <li>Course progress and completion records</li>
                <li>Certificates and achievements</li>
                <li>Payment and subscription information</li>
                <li>Support tickets and communication history</li>
                <li>Any uploaded content or assignments</li>
                <li>Preferences and settings</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
              <p className="text-gray-700 mb-4">
                In certain circumstances, we may retain some data for legal or regulatory purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li>Financial records required for tax or accounting purposes</li>
                <li>Data required for legal compliance or dispute resolution</li>
                <li>Anonymized usage data for service improvement (with no personal identifiers)</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Processing Time</h2>
              <p className="text-gray-700 mb-8">
                Account deletion requests are typically processed within 30 days of receipt. 
                You will receive a confirmation email once your data has been successfully deleted.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
              <p className="text-gray-700 mb-4">
                Under applicable privacy laws, you have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li>Request access to your personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your personal data</li>
                <li>Request restriction of data processing</li>
                <li>Request data portability</li>
                <li>Object to data processing</li>
              </ul>

              <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      <strong>Need Help?</strong> If you have any questions about data deletion or your privacy rights, 
                      please don't hesitate to contact our privacy team at <a href="mailto:privacy@codecoast.org" className="text-green-700 underline">privacy@codecoast.org</a>
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center pt-8 border-t border-gray-200">
                <p className="text-gray-500 text-sm">
                  Last updated: {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <p className="text-gray-400">
              &copy; {new Date().getFullYear()} Code Coast. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DeletionInstructions;
